import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  DialogActions,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import QRCode from "qrcode";
import { usePermissions } from "../login/PermissionContext";

export default function PaymentModal({
  open,
  onClose,
  appointment,
  selectedDoctorData,
  onStatusUpdated,
  onAfterClose,
}) {
  const { userInfo } = usePermissions();
  const [paymentType, setPaymentType] = useState("UPI");
  const [isPaid, setIsPaid] = useState(false);
  const [isRefundCompleted, setIsRefundCompleted] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("500.00");
  const [doctorFee, setDoctorFee] = useState("");
  const [facilityDoctorsMap, setFacilityDoctorsMap] = useState({});
  const [qrUrl, setQrUrl] = useState("");

  // Source UPI ID and facility identifier (used as payee name) from logged-in user info
  // Default UPI to test@123 when upi_id is null/undefined
  const upiId = userInfo?.upi_id;
  console.log("appointment--", appointment);
  console.log("upiId--", upiId);

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = { Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    if (open && appointment) {
      const paid = (appointment?.payment_status || "").toUpperCase() === "PAID";
      setIsPaid(paid);
      const refundCompleted =
        (appointment?.payment_status || "").toUpperCase() === "REFUND";
      setIsRefundCompleted(refundCompleted);
    }
  }, [open, appointment]);

  useEffect(() => {
    // Generate QR only when we have UPI ID and UPI is selected
    if (open && paymentType === "UPI" && upiId) {
      const upiString = `upi://pay?pa=${encodeURIComponent(
        upiId
      )}&cu=INR&am=${encodeURIComponent(paymentAmount || "500.00")}`;
      QRCode.toDataURL(upiString, { width: 256, margin: 1 })
        .then((url) => setQrUrl(url))
        .catch(() => setQrUrl(""));
    } else {
      setQrUrl("");
    }
  }, [open, paymentType, paymentAmount, upiId]);

  // Fetch facility specific doctors (includes fees) when modal opens
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_facility_specific_doctors/`, {
        headers,
      })
      .then((response) => {
        if (
          response?.data?.status === "Success" &&
          Array.isArray(response.data.doctor_list)
        ) {
          const map = {};
          response.data.doctor_list.forEach((doc) => {
            if (doc?.doctor_id) map[doc.doctor_id] = doc;
          });
          setFacilityDoctorsMap(map);
        }
      })
      .catch(() => {
        // silent fail; leave defaults
      });
  }, [open, REACT_APP_BACKEND_ROUTE_URL]); // headers is stable enough from sessionStorage; avoid re-fetching excessively

  // When we have doctors map and a doctor id, set the fee and default amount
  useEffect(() => {
    if (!open) return;
    const currentDoctorId =
      (selectedDoctorData && selectedDoctorData.doctor_id) ||
      sessionStorage.getItem("doctor_id");
    if (!currentDoctorId) return;
    const feeValue = facilityDoctorsMap?.[currentDoctorId]?.fees;
    if (
      feeValue !== undefined &&
      feeValue !== null &&
      !Number.isNaN(Number(feeValue))
    ) {
      const feeString = Number(feeValue).toFixed(2);
      setDoctorFee(feeString);
      // If amount is untouched or different from new fee, set it to fee
      setPaymentAmount(feeString);
    } else {
      setDoctorFee("");
    }
  }, [open, selectedDoctorData, facilityDoctorsMap]);

  const updatePaymentStatus = async (nextPaid) => {
    try {
      if (!appointment) {
        setIsPaid(nextPaid);
        return;
      }
      const doctorIdToUse =
        (selectedDoctorData && selectedDoctorData.doctor_id) ||
        sessionStorage.getItem("doctor_id");
      if (!doctorIdToUse) {
        setIsPaid(nextPaid);
        return;
      }
      setIsUpdatingPayment(true);
      const payload = {
        // doctor_id: doctorIdToUse,
        // patient_id: appointment.patient,
        appointment_id: appointment.appointment_id,
        payment_status: nextPaid ? "PAID" : "UNPAID",
        payment_amount: paymentAmount,
        payment_mode: paymentType,
      };
      const resp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
        payload,
        { headers }
      );
      if (resp?.data?.status === "Success") {
        setIsPaid(nextPaid);
        onStatusUpdated &&
          onStatusUpdated(
            payload.payment_status,
            payload.payment_amount,
            payload.payment_mode
          );
        if (nextPaid) {
          onClose();
        }
      } else {
        // do not update local status if backend did not confirm success
      }
    } catch (e) {
      setIsPaid((prev) => !nextPaid);
    } finally {
      setIsUpdatingPayment(false);
    }
  };
  const updateRefundStatus = async () => {
    try {
      setIsUpdatingPayment(true);
      const payload = {
        // doctor_id: doctorIdToUse,
        // patient_id: appointment.patient,
        appointment_id: appointment.appointment_id,
        payment_status: "REFUND",
        payment_amount: paymentAmount,
        payment_mode: paymentType,
      };
      const resp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
        payload,
        { headers }
      );
      if (resp?.data?.status === "Success") {
        // setIsPaid(nextPaid);
        onStatusUpdated &&
          onStatusUpdated(
            payload.payment_status,
            payload.payment_amount,
            payload.payment_mode
          );
        onClose();
      } else {
        // do not update local status if backend did not confirm success
      }
    } catch (e) {
      // setIsPaid((prev) => !nextPaid);
      console.log(e);
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof onAfterClose === "function") {
      onAfterClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "90%",
          maxWidth: "600px",
          // height: "80vh",
          height: "auto",
          zIndex: 9997,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2,
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#FFFFFF",
            margin: 0,
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          Process Payment
        </div>
        <IconButton onClick={handleClose}>
          <CloseRoundedIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 2.5, maxHeight: "calc(80vh - 100px)", overflowY: "auto" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {appointment?.payment_status === null ||
          appointment?.payment_status === "" ||
          appointment?.payment_status === "UNPAID" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                    }}
                  >
                    Payment Type
                  </label>
                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="form-control"
                    style={{ maxWidth: 280 }}
                  >
                    <option value="UPI">UPI</option>
                    <option value="CASH">Cash</option>
                    <option value="CREDIT CARD">Credit Card</option>
                    <option value="DEBIT CARD">Debit Card</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="form-control"
                    style={{ maxWidth: 280 }}
                    disabled
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      marginBottom: "20px",
                    }}
                  >
                    Payment Status
                  </label>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          isPaid || isRefundCompleted ? "#0b3d0b" : "#5D3A00",
                        backgroundColor:
                          isPaid || isRefundCompleted ? "#A1DA3B" : "#FFBD8A",
                        border: "1px solid #BAE0DD",
                        textTransform: "capitalize",
                        width: "fit-content",
                        marginBottom: "10px",
                      }}
                    >
                      {/* {isPaid ? "paid" : "unpaid"} */}

                      {appointment?.payment_status === "PAID"
                        ? "Paid"
                        : appointment?.payment_status === "INITIATE REFUND"
                        ? "Refund Initiated"
                        : appointment?.payment_status === "REFUND"
                        ? "Refund Completed"
                        : "Unpaid"}
                    </Box>
                  </Box>
                </div>
              </Box>

              {paymentType === "UPI" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "center", sm: "flex-start" },
                    gap: 2,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    p: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {upiId === null || upiId === undefined ? (
                    <div
                      style={{
                        width: 180,
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f3f4f6",
                        color: "#6b7280",
                      }}
                    >
                      QR Code Not Available
                    </div>
                  ) : qrUrl ? (
                    <img
                      src={qrUrl}
                      alt="UPI QR Code"
                      style={{ width: 180, height: 180 }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 180,
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f3f4f6",
                        color: "#6b7280",
                      }}
                    >
                      Generating QR...
                    </div>
                  )}
                  <div>
                    <div
                      className="patient-name"
                      style={{
                        marginBottom: "8px",
                        fontWeight: 600,
                        color: "#1c2c4c",
                      }}
                    >
                      Pay consultation fee via UPI
                    </div>
                    <div
                      className="patient-detail"
                      style={{ marginBottom: "4px" }}
                    >
                      UPI ID: {upiId || "Not Available"}
                    </div>
                    <div className="patient-detail">
                      Amount: ₹{paymentAmount || "500.00"}
                    </div>
                  </div>
                </Box>
              )}
            </>
          ) : appointment?.payment_status === "PAID" ? (
            <>
              <Typography>
                This appointment has already been paid, and the payment of Rs.
                {paymentAmount} has been successfully recorded. If this was
                marked as paid by mistake, you can click on ‘Mark as Unpaid’ to
                correct it.
              </Typography>
            </>
          ) : appointment?.payment_status === "INITIATE REFUND" ? (
            <>
              <Typography>
                The refund process has been initiated. Please mark the refund as
                completed once you have confirmed that the refund has been
                successfully processed.
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                The refund process is successfully completed.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {" "}
        {appointment?.payment_status !== "INITIATE REFUND" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1,
            }}
          >
            <button
              type="button"
              onClick={() => updatePaymentStatus(!isPaid)}
              className="btn btn__secondary btn__rounded"
              disabled={isUpdatingPayment}
            >
              {isUpdatingPayment
                ? "Updating..."
                : isPaid
                ? "Mark as Unpaid"
                : "Mark as Paid"}
            </button>
          </Box>
        ) : null}
        {appointment?.payment_status === "INITIATE REFUND" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1,
            }}
          >
            <button
              type="button"
              onClick={() => updateRefundStatus()}
              className="btn btn__secondary btn__rounded"
              disabled={isUpdatingPayment}
            >
              {isUpdatingPayment ? "Updating..." : "Mark as Refund Completed"}
            </button>
          </Box>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
